import { RefObject, useEffect } from 'react';

type ClickOutsideHandler = (event: MouseEvent | TouchEvent) => void;

interface UseOnClickOutsideOptions {
  capture?: boolean;
  passive?: boolean;
  once?: boolean;
}

export const useOnClickOutside = (
  handler: ClickOutsideHandler | null,
  mainRef: RefObject<HTMLElement> | null,
  options?: UseOnClickOutsideOptions | RefObject<HTMLElement>,
  ...secondaryRefs: Array<RefObject<HTMLElement>>
): void => {
  useEffect(() => {
    if (!handler || !mainRef) {
      return;
    }

    let eventOptions: UseOnClickOutsideOptions | undefined;
    let refs = secondaryRefs;

    if (options) {
      if ('current' in options) {
        refs = [options, ...secondaryRefs];
      } else {
        eventOptions = options;
      }
    }

    const listener = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;

      if (
        !mainRef?.current ||
        mainRef.current?.contains(target) ||
        refs.some((ref) => ref.current?.contains(target))
      ) {
        return;
      }

      handler(event);
    };

    document.addEventListener('mousedown', listener, eventOptions);
    document.addEventListener('touchstart', listener, eventOptions);

    // eslint-disable-next-line consistent-return
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [handler, mainRef, options, ...secondaryRefs]);
};
